import React, { useState } from "react";
import { useApp } from "../context/AppContext";
import { formatPrice, formatDate } from "../utils/helpers";
import { StarDisplay, StarPicker } from "../components/StarRating";
import ChatModal from "../components/ChatModal";

export default function MyPurchases() {
  const { getMyPurchases, currentUser, addReview, hasReviewed, getOrCreateChat } = useApp();
  const [showCode, setShowCode]         = useState({});
  const [showReview, setShowReview]     = useState({});
  const [rating, setRating]             = useState({});
  const [comment, setComment]           = useState({});
  const [reviewLoading, setReviewLoading] = useState({});
  const [reviewDone, setReviewDone]     = useState({});
  const [openChatId, setOpenChatId]     = useState(null);

  const handleContact = async (sale) => {
    const chat = await getOrCreateChat(sale.productId, currentUser.id);
    if (chat) setOpenChatId(chat.id);
  };

  const purchases = getMyPurchases();
  if (!currentUser) return null;

  const toggleCode = (id) => setShowCode(prev => ({...prev, [id]: !prev[id]}));

  const handleReview = async (sale) => {
    if (!rating[sale.id]) { alert("Selecciona una calificacion"); return; }
    setReviewLoading(prev => ({...prev, [sale.id]: true}));
    try {
      await addReview(sale.id, rating[sale.id], comment[sale.id] || "");
      setReviewDone(prev => ({...prev, [sale.id]: true}));
      setShowReview(prev => ({...prev, [sale.id]: false}));
    } catch(e) {
      alert(e.message);
    } finally {
      setReviewLoading(prev => ({...prev, [sale.id]: false}));
    }
  };

  return (
    <div style={s.page}>
      <h1 style={s.heading}>Mis compras</h1>
      {purchases.length === 0 ? (
        <div style={s.empty}>
          <span style={{fontSize:56}}>🛒</span>
          <p style={s.emptyTitle}>Todavia no compraste nada</p>
        </div>
      ) : (
        <div style={s.list}>
          {purchases.map(sale => {
            const imgSrc = sale.images?.[0]?.url || null;
            const alreadyReviewed = hasReviewed(sale.id) || reviewDone[sale.id];
            return (
              <div key={sale.id} style={s.card}>
                <div style={s.cardInner}>
                  {imgSrc
                    ? <img src={imgSrc} alt={sale.productTitle} style={s.img} />
                    : <div style={s.noImg}>📷</div>
                  }
                  <div style={s.info}>
                    <p style={s.productTitle}>{sale.productTitle}</p>
                    <div style={s.tags}>
                      {sale.color && <span style={s.tag}>🎨 {sale.color}</span>}
                      {sale.cantidad > 1 && <span style={s.tag}>📦 {sale.cantidad} unidades</span>}
                      <span style={s.tag}>💳 Mercado Pago</span>
                      <span style={s.tag}>📅 {formatDate(sale.date)}</span>
                    </div>
                    <p style={s.seller}>Vendido por {sale.sellerName?.split(" ")[0]}</p>
                    {sale.cantidad > 1 && <p style={s.priceUnit}>{formatPrice(sale.pricePerUnit)} c/u</p>}
                    <p style={s.totalPrice}>{formatPrice(sale.price)}</p>
                  </div>
                </div>

                <div style={s.statusRow}>
                  {sale.status === "pending" ? (
                    <div style={s.pendingBox}>
                      <p style={s.pendingTitle}>⏳ Esperando entrega</p>
                      <p style={s.pendingText}>Cuando el vendedor te entregue el producto, te va a pedir un codigo. Mostráselo solo cuando tengas el producto en mano.</p>
                      <div style={{display:"flex",gap:10,flexWrap:"wrap"}}>
                        <button style={s.btnShowCode} onClick={() => toggleCode(sale.id)}>
                          {showCode[sale.id] ? "Ocultar codigo" : "🔑 Ver codigo de entrega"}
                        </button>
                        <button style={s.btnContact} onClick={() => handleContact(sale)}>💬 Contactar al vendedor</button>
                      </div>
                      {showCode[sale.id] && (
                        <div style={s.codeBox}>
                          <p style={s.codeLabel}>Tu codigo de entrega</p>
                          <p style={s.codeNumber}>{sale.deliveryCode}</p>
                          <p style={s.codeWarning}>⚠️ Mostráselo al vendedor solo cuando tengas el producto en mano</p>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div style={s.receivedSection}>
                      <div style={s.receivedBox}>
                        <p style={s.receivedText}>✅ Entrega confirmada el {formatDate(sale.receivedDate)}</p>
                      </div>
                      <button style={{...s.btnContact,alignSelf:"flex-start"}} onClick={() => handleContact(sale)}>💬 Contactar al vendedor</button>

                      {/* Calificacion */}
                      {!alreadyReviewed ? (
                        <div style={s.reviewSection}>
                          {!showReview[sale.id] ? (
                            <button style={s.btnReview} onClick={() => setShowReview(prev => ({...prev, [sale.id]: true}))}>
                              ⭐ Calificar a {sale.sellerName.split(" ")[0]}
                            </button>
                          ) : (
                            <div style={s.reviewBox}>
                              <p style={s.reviewTitle}>Califica tu experiencia con {sale.sellerName.split(" ")[0]}</p>
                              <StarPicker value={rating[sale.id]||0} onChange={v => setRating(prev => ({...prev, [sale.id]: v}))} />
                              <textarea
                                style={s.reviewComment}
                                placeholder="Contanos como fue la experiencia (opcional)..."
                                rows={3}
                                value={comment[sale.id]||""}
                                onChange={e => setComment(prev => ({...prev, [sale.id]: e.target.value}))}
                              />
                              <div style={s.reviewBtns}>
                                <button style={s.btnCancelReview} onClick={() => setShowReview(prev => ({...prev, [sale.id]: false}))}>Cancelar</button>
                                <button
                                  style={{...s.btnSubmitReview, opacity: reviewLoading[sale.id]?0.7:1}}
                                  onClick={() => handleReview(sale)}
                                  disabled={reviewLoading[sale.id]}
                                >
                                  {reviewLoading[sale.id] ? "Guardando..." : "Enviar calificacion"}
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      ) : (
                        <p style={s.reviewedMsg}>✅ Ya calificaste esta compra</p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
      {openChatId && <ChatModal chatId={openChatId} onClose={() => setOpenChatId(null)} />}
    </div>
  );
}

const s = {
  page:{maxWidth:800,margin:"0 auto",padding:"24px 16px"},
  heading:{fontSize:22,fontWeight:600,marginBottom:20},
  empty:{textAlign:"center",padding:"80px 0",display:"flex",flexDirection:"column",alignItems:"center",gap:12},
  emptyTitle:{fontSize:18,fontWeight:500,color:"#333"},
  list:{display:"flex",flexDirection:"column",gap:16},
  card:{background:"#fff",border:"0.5px solid #e0e0e0",borderRadius:14,overflow:"hidden"},
  cardInner:{display:"flex"},
  img:{width:120,height:120,objectFit:"cover",flexShrink:0},
  noImg:{width:120,height:120,background:"#f5f5f5",display:"flex",alignItems:"center",justifyContent:"center",fontSize:32,flexShrink:0},
  info:{padding:"14px",flex:1},
  productTitle:{fontSize:15,fontWeight:600,color:"#111",marginBottom:6},
  tags:{display:"flex",flexWrap:"wrap",gap:5,marginBottom:6},
  tag:{fontSize:11,background:"#f4f4f4",color:"#555",padding:"3px 8px",borderRadius:20},
  seller:{fontSize:12,color:"#888",marginBottom:4},
  priceUnit:{fontSize:12,color:"#aaa",marginBottom:2},
  totalPrice:{fontSize:18,fontWeight:700,color:"#1D9E75"},
  statusRow:{borderTop:"0.5px solid #f0f0f0",padding:"16px"},
  pendingBox:{display:"flex",flexDirection:"column",gap:10},
  pendingTitle:{fontSize:14,fontWeight:600,color:"#b7830a"},
  pendingText:{fontSize:13,color:"#555",lineHeight:1.5},
  btnShowCode:{alignSelf:"flex-start",background:"#111",color:"#fff",border:"none",borderRadius:10,padding:"10px 18px",fontSize:14,fontWeight:600,cursor:"pointer"},
  btnContact:{background:"#f0f0f0",color:"#333",border:"none",borderRadius:10,padding:"10px 18px",fontSize:14,cursor:"pointer"},
  codeBox:{background:"#f0faf6",border:"2px solid #1D9E75",borderRadius:12,padding:"16px",textAlign:"center"},
  codeLabel:{fontSize:12,color:"#888",marginBottom:8,fontWeight:500},
  codeNumber:{fontSize:42,fontWeight:700,color:"#1D9E75",letterSpacing:8,marginBottom:8},
  codeWarning:{fontSize:12,color:"#b7830a",lineHeight:1.4},
  receivedSection:{display:"flex",flexDirection:"column",gap:12},
  receivedBox:{background:"#E1F5EE",borderRadius:10,padding:"10px 14px"},
  receivedText:{fontSize:13,fontWeight:600,color:"#0F6E56"},
  reviewSection:{},
  btnReview:{background:"#f59e0b",color:"#fff",border:"none",borderRadius:10,padding:"10px 18px",fontSize:14,fontWeight:600,cursor:"pointer"},
  reviewBox:{background:"#fffbeb",border:"0.5px solid #f0c040",borderRadius:12,padding:"16px",display:"flex",flexDirection:"column",gap:10},
  reviewTitle:{fontSize:14,fontWeight:600,color:"#333"},
  reviewComment:{width:"100%",padding:"10px 12px",borderRadius:8,border:"0.5px solid #ddd",fontSize:14,resize:"vertical"},
  reviewBtns:{display:"flex",gap:10},
  btnCancelReview:{flex:1,padding:"10px 0",border:"0.5px solid #ddd",borderRadius:8,background:"#fff",cursor:"pointer",fontSize:13,color:"#555"},
  btnSubmitReview:{flex:1,padding:"10px 0",background:"#f59e0b",color:"#fff",border:"none",borderRadius:8,fontSize:13,fontWeight:600,cursor:"pointer"},
  reviewedMsg:{fontSize:13,color:"#1D9E75",fontWeight:500},
};
